import type { Jsonify } from "type-fest"
import { NIVEAUX_POUR_LBA, TRAINING_CONTRACT_TYPE } from "../constants/recruteur.js"
import dayjs from "../helpers/dayjs.js"
import { extensions } from "../helpers/zod-helpers/zod-primitives.js"
import { z } from "../helpers/zod-with-open-api.js"
import { assertUnreachable } from "../utils/assert-unreachable.js"
import { detectUrlAndEmails, detectUrls } from "../utils/detect-url-and-emails.js"
import { zObjectId } from "./common.js"

import { ZReferentielRomeForJob, ZRomeCompetence } from "./rome.model.js"

export enum JOB_STATUS {
  ACTIVE = "Active",
  POURVUE = "Pourvue",
  ANNULEE = "Annulée",
  EN_ATTENTE = "En attente",
  EN_MODERATION = "En modération",
}

/**
 * `EN_ATTENTE` attend la validation du compte ou de l'e-mail, `EN_MODERATION` une décision humaine sur
 * le contenu (#5498) : valider le compte publie la première, jamais la seconde (cf. checkForJobActivations).
 */
export enum JOB_STATUS_ENGLISH {
  ACTIVE = "Active",
  POURVUE = "Filled",
  ANNULEE = "Cancelled",
  EN_ATTENTE = "Pending",
  EN_MODERATION = "InReview",
}

/** Statuts du contrat de l'API publique : une offre en modération n'y est ni lue ni écrite. */
export const JOB_STATUS_ENGLISH_PUBLIC = {
  ACTIVE: JOB_STATUS_ENGLISH.ACTIVE,
  POURVUE: JOB_STATUS_ENGLISH.POURVUE,
  ANNULEE: JOB_STATUS_ENGLISH.ANNULEE,
  EN_ATTENTE: JOB_STATUS_ENGLISH.EN_ATTENTE,
} as const

export type JOB_STATUS_ENGLISH_PUBLIC = (typeof JOB_STATUS_ENGLISH_PUBLIC)[keyof typeof JOB_STATUS_ENGLISH_PUBLIC]

/** Une offre en modération n'a pas de représentation publique : la sélectionner est une erreur de l'appelant. */
export function toPublicJobStatus(status: JOB_STATUS_ENGLISH): JOB_STATUS_ENGLISH_PUBLIC {
  if (status === JOB_STATUS_ENGLISH.EN_MODERATION) {
    throw new Error("inattendu : une offre en modération ne doit pas être exposée")
  }
  return status
}

/**
 * Qui a clôturé une offre, et par quel canal. Reporté dans `offer_status_history[].granted_by`,
 * `reason` portant le motif choisi dans la modale.
 *
 * Le canal vient de la route : lien signé reçu par mail (auth access-token) ou espace pro connecté
 * (auth cookie-session). Sur l'espace pro, l'acteur est déduit des rôles de la session
 * (cf. resolveEspaceProClosureOrigin) : les quatre rôles passent par la même route, et ne pas les
 * distinguer revenait à ranger l'annulation d'un administrateur avec celle du recruteur lui-même.
 *
 * Les valeurs ESPACE_PRO_* ne concernent que les offres OFFRES_EMPLOI_LBA, seules gérées depuis
 * l'espace pro. MAIL_RECRUTEUR couvre en plus les offres partenaires : le lien envoyé au recruteur
 * est le même, seule la route appelée derrière change selon le type d'offre.
 */
export enum JOB_CLOSURE_ORIGIN {
  /**
   * Routes en access-token, atteintes depuis la page `(from-mail)` où le lien du mail dépose le
   * recruteur : PUT /formulaire/offre/:jobId/cancel et /provided pour les offres LBA, POST
   * /v2/_private/jobs/canceled|provided/:id pour les offres partenaires. Un même jeton, signé par
   * createCancelJobLink / createProvidedJobLink, porte les scopes des deux familles de routes et est
   * lié à l'identifiant de l'offre : malgré leur préfixe v2, ces routes ne sont pas une API ouverte
   * aux partenaires. Libellé volontairement neutre : le même canal porte l'annulation et la
   * déclaration « offre pourvue ».
   */
  MAIL_RECRUTEUR = "action du recruteur depuis un mail",
  ESPACE_PRO_RECRUTEUR = "clôture par le recruteur depuis l'espace pro",
  ESPACE_PRO_CFA = "clôture par le CFA délégataire depuis l'espace pro",
  ESPACE_PRO_OPCO = "clôture par l'OPCO depuis l'espace pro",
  ESPACE_PRO_ADMIN = "clôture par un administrateur depuis l'espace pro",
  /** Repli quand la session n'expose aucun rôle exploitable : le canal reste sûr, pas l'acteur. */
  ESPACE_PRO_INDETERMINE = "clôture depuis l'espace pro (acteur indéterminé)",
}

export const JOB_START_TYPE = {
  DES_QUE_POSSIBLE: "des_que_possible",
  PRECISE_DATE: "precise_date",
} as const

export type JOB_START_TYPE = (typeof JOB_START_TYPE)[keyof typeof JOB_START_TYPE]

/** Mode de rédaction de la description d'une offre LBA : fiche métier ROME, ou texte rédigé par le recruteur. */
export const OFFER_DESCRIPTION_MODE = {
  STRUCTURED: "structured",
  CUSTOM: "custom",
} as const

export type OFFER_DESCRIPTION_MODE = (typeof OFFER_DESCRIPTION_MODE)[keyof typeof OFFER_DESCRIPTION_MODE]

export const JOB_DESCRIPTION_MAX_LENGTH = 3000
export const JOB_EMPLOYER_DESCRIPTION_MAX_LENGTH = 800

export function translateJobStatus(status: JOB_STATUS): JOB_STATUS_ENGLISH | undefined {
  switch (status) {
    case JOB_STATUS.ACTIVE:
      return JOB_STATUS_ENGLISH.ACTIVE
    case JOB_STATUS.POURVUE:
      return JOB_STATUS_ENGLISH.POURVUE
    case JOB_STATUS.ANNULEE:
      return JOB_STATUS_ENGLISH.ANNULEE
    case JOB_STATUS.EN_ATTENTE:
    case JOB_STATUS.EN_MODERATION:
      return undefined
    default:
      assertUnreachable(status)
  }
}

export function traductionJobStatus(status: JOB_STATUS_ENGLISH): JOB_STATUS {
  switch (status) {
    case JOB_STATUS_ENGLISH.ACTIVE:
      return JOB_STATUS.ACTIVE
    case JOB_STATUS_ENGLISH.POURVUE:
      return JOB_STATUS.POURVUE
    case JOB_STATUS_ENGLISH.ANNULEE:
      return JOB_STATUS.ANNULEE
    case JOB_STATUS_ENGLISH.EN_ATTENTE:
      return JOB_STATUS.EN_ATTENTE
    case JOB_STATUS_ENGLISH.EN_MODERATION:
      return JOB_STATUS.EN_MODERATION
    default: {
      assertUnreachable(status)
    }
  }
}

export const ZJobType = z.array(extensions.buildEnum(TRAINING_CONTRACT_TYPE)).describe("Type de contrat")

export const ZDelegation = z.strictObject({
  siret_code: z.string().describe("SIRET de l'établissement"),
  email: z.string().describe("Email gestionnaire de l'établissement"),
  cfa_read_company_detail_at: z.date().nullish().describe("Date de consultation de l'offre"),
  etablissement_id: z.string().nullish().describe("Identifiant d'établissement du catalogue correspondant à etablissement_gestionnaire_id ou etablissement_formateur_id"),
})

export const TO_APPLICANT_QUESTION_MIN_LENGTH = 5
export const TO_APPLICANT_QUESTION_MAX_LENGTH = 200
export const TO_APPLICANT_QUESTIONS_MAX_COUNT = 3

// Questions posées par le recruteur au candidat. Définies ici et réutilisées par le modèle jobs_partners
// pour que la saisie espace-pro et le dépôt par API partagent exactement les mêmes contraintes.
export const ZToApplicantQuestions = z
  .array(
    z
      .string()
      .trim()
      .min(TO_APPLICANT_QUESTION_MIN_LENGTH, `Une question doit contenir au moins ${TO_APPLICANT_QUESTION_MIN_LENGTH} caractères.`)
      .max(TO_APPLICANT_QUESTION_MAX_LENGTH, `Une question ne peut pas dépasser ${TO_APPLICANT_QUESTION_MAX_LENGTH} caractères.`)
      .refine((value) => detectUrlAndEmails(value).length === 0, "Les urls et les emails sont interdits")
  )
  .max(TO_APPLICANT_QUESTIONS_MAX_COUNT, `Sélectionnez ${TO_APPLICANT_QUESTIONS_MAX_COUNT} questions au maximum`)
  .nullish()
  .describe("Questions posées par le recruteur pour le candidat")

export const ZJobFields = z.strictObject({
  rome_label: z.string().nullish().describe("Libellé du métier concerné"),
  rome_appellation_label: z.string().nullish().describe("Libellé de l'appelation ROME"),
  job_level_label: extensions.buildEnum(NIVEAUX_POUR_LBA).nullish().describe("Niveau de formation visé en fin de stage"),
  job_start_date: z.coerce.date<Date>().describe("Date de début de l'alternance"),
  job_start_type: extensions.buildEnum(JOB_START_TYPE).nullish().describe("Mode de démarrage du contrat"),
  job_start_date_flexible: z.boolean().nullish().describe("Indique si la date de début est flexible"),
  job_description: z.string().nullish().describe("Description de l'offre d'alternance - minimum 30 charactères si rempli"),
  job_employer_description: z.string().nullish().describe("Description de l'employer proposant l'offre d'alternance - minimum 30 charactères si rempli"),
  rome_code: z.array(z.string()).describe("Liste des romes liés au métier"),
  rome_detail: ZReferentielRomeForJob.nullish().describe("Détail du code ROME selon la nomenclature Pole emploi"),
  job_creation_date: z.date().nullish().describe("Date de creation de l'offre"),
  job_expiration_date: z.date().nullish().describe("Date d'expiration de l'offre"),
  job_update_date: z.date().nullish().describe("Date de dernière mise à jour de l'offre"),
  job_last_prolongation_date: z.date().nullish().describe("Date de dernière prolongation de l'offre"),
  job_prolongation_count: z.number().nullish().describe("Nombre de fois où l'offre a été prolongée"),
  relance_mail_expiration_J7: z.date().nullish().describe("Date de l'envoi du mail de relance avant expiration à J-7"),
  relance_mail_expiration_J1: z.date().nullish().describe("Date de l'envoi du mail de relance avant expiration à J-1"),
  job_status: extensions.buildEnum(JOB_STATUS).describe("Statut de l'offre"),
  job_status_comment: z.string().nullish().describe("Raison de la suppression de l'offre"),
  job_type: ZJobType,
  job_delegation_count: z.number().nullish().describe("Nombre de délégations"),
  delegations: z.array(ZDelegation).nullish().describe("Liste des délégations"),
  is_disabled_elligible: z.boolean().nullish().default(false).describe("Poste ouvert aux personnes en situation de handicap"),
  job_count: z.number().nullish().default(1).describe("Nombre de poste(s) ouvert(s) pour cette offre"),
  job_duration: z.number().min(6).max(36).nullish().describe("Durée du contrat en mois"),
  job_rythm: z
    .string()
    .max(100)
    .refine((value) => detectUrls(value).length === 0, "Les URLs sont interdites")
    .nullish()
    .describe("Répartition de la présence de l'alternant en formation/entreprise"),
  custom_address: z.string().nullish().describe("Adresse personnalisée de l'entreprise"),
  custom_geo_coordinates: z.string().nullish().describe("Latitude/Longitude de l'adresse personnalisée de l'entreprise"),
  custom_job_title: z.string().nullish().describe("Titre personnalisée de l'offre"), // TODO: à supprimer
  stats_detail_view: z.number().nullish().describe("Nombre de vues de la page de détail"),
  stats_search_view: z.number().nullish().describe("Nombre de vues sur une page de recherche"),
  competences_rome: ZRomeCompetence.nullish().describe("Compétences du code ROME sélectionnées par le recruteur"),
  mer_sent: z.date().nullish().describe("Date d'envoi de la mise en relation"),
  offer_title_custom: z
    .string()
    .min(3, "L’intitulé est trop court. Sa taille doit être comprise entre 3 et 150 caractères.")
    .max(150, "L’intitulé est trop long. Sa taille doit être comprise entre 3 et 150 caractères.")
    .nullish()
    .refine((value: string | null | undefined) => (value ? detectUrlAndEmails(value).length === 0 : true), "Les urls et les emails sont interdits")
    .describe("Titre de l'offre saisi par le recruteur"),
  to_applicant_questions: ZToApplicantQuestions,
  ft_support: z.boolean().nullish().default(false).describe("Offre transmise à France Travail"),
})

export const ZJob = ZJobFields.extend({
  _id: zObjectId,
}).strict()

export const ZJobWithRomeDetail = ZJob.extend({
  rome_detail: ZReferentielRomeForJob.nullish(),
}).strict()

export const ZJobStartDateCreate = (now: dayjs.Dayjs | null = null) =>
  // Le changement de jour se fait à minuit (heure de Paris)
  ZJobFields.shape.job_start_date
    .refine(
      (date) => {
        const startOfDay = dayjs.tz(now ?? dayjs.tz()).startOf("days")
        return dayjs.tz(date).isSameOrAfter(startOfDay)
      },
      {
        message: "job_start_date must be greater or equal to today's date",
      }
    )
    .refine(
      (date) => {
        return dayjs.tz(date).isSameOrBefore(dayjs().add(2, "years"))
      },
      {
        message: "job_start_date must be lower or equal to today's date + 2 years",
      }
    )

// Limites de saisie portées par les corps de création et de mise à jour, pas par ZJobFields qui valide
// aussi les réponses : sanitizeTextField encode & en &amp;, un texte stocké peut dépasser sa limite.
export const ZJobFreeTextInput = {
  job_description: z.string().max(JOB_DESCRIPTION_MAX_LENGTH).nullish(),
  job_employer_description: z.string().max(JOB_EMPLOYER_DESCRIPTION_MAX_LENGTH).nullish(),
}

export const ZJobCreate = ZJobFields.pick({
  rome_appellation_label: true,
  rome_code: true,
  rome_label: true,
  job_type: true,
  job_level_label: true,
  job_count: true,
  job_duration: true,
  job_rythm: true,
  delegations: true,
  competences_rome: true,
  offer_title_custom: true,
  to_applicant_questions: true,
  ft_support: true,
  job_start_type: true,
  job_start_date_flexible: true,
})
  .extend({
    ...ZJobFreeTextInput,
    job_start_date: ZJobStartDateCreate(),
    job_start_type: extensions.buildEnum(JOB_START_TYPE),
  })
  .strict()

export type IDelegation = z.output<typeof ZDelegation>

export type IJob = z.output<typeof ZJob>
export type IJobCreate = z.output<typeof ZJobCreate>
export type IJobWithRomeDetail = z.output<typeof ZJobWithRomeDetail>
export type IJobJson = Jsonify<z.input<typeof ZJob>>

export const ZNewDelegations = z.strictObject({
  etablissementCatalogueIds: z.array(z.string()),
})

export type INewDelegations = z.input<typeof ZNewDelegations>
